import fetchClient from "./fetchClient";
import { getEnvironment360BizURL, getFetchApiOptions, ApiConfig } from '../configs/appConfig';
import { getHeaders } from "./dataService";

export interface AccessCodeResult {
    Recno: number;
    Code: string;
    /** Already formatted by the backend as "Code - Description". */
    Description: string;
    /** Law code of the default paragraph (not a recno). */
    DefaultParagraph: string;
    IsParagraphRequired: boolean;
}

export interface LawCodeResult {
    Code: string;
    Description: string;
}

export interface AccessGroupResult {
    Recno: number;
    Name: string;
    IsPublic: boolean;
}

interface AccessCodeResultType {
    Recno: number;
    Code: string;
    Description: string;
    RequireParagraphKey: number;
    RequireDegradeKey?: number | null;
    DefaultAccessParagraph?: string | null;
    DowngradeYears?: number | null;
}

interface AccessGroupResultType {
    Recno: number;
    Code: string;
    Name?: string | null;
    Description?: string | null;
    IsAccessGroup: number;
    IsVisibleForAllUsers?: number | null;
}

interface AccessCodeAuthorization {
    Recno: number;
    Code: string;
    Description: string;
    ToAccessCodeKey: number;
    SortId?: number | null;
    Years?: number | null;
}

export const getAccessCodes = async (documentArchive: number): Promise<AccessCodeResult[]> => {
    const headers = await getHeaders();
    try {
        const result = await fetchClient(getEnvironment360BizURL() +
            ApiConfig.GetAccessCodes, {
            headers: headers,
            body: JSON.stringify({ documentArchive: documentArchive }),
            method: "POST",
            ...getFetchApiOptions()
        }) as AccessCodeResultType[];

        return result.map(ac => ({
            Recno: ac.Recno,
            Code: ac.Code,
            Description: ac.Description,
            DefaultParagraph: ac.DefaultAccessParagraph ?? '',
            IsParagraphRequired: ac.RequireParagraphKey === -1
        }));

    } catch (err) {
        throw new Error("Error fetching access codes: " + String(err));
    }
};

export const getParagraphsForAccessCode = async (accessCode: number): Promise<LawCodeResult[]> => {
    const headers = await getHeaders();
    try {
        const result = await fetchClient(getEnvironment360BizURL() +
            ApiConfig.GetParagraphsForAccessCode, {
            headers: headers,
            body: JSON.stringify({ accessCode: accessCode }),
            method: "POST",
            ...getFetchApiOptions()
        }) as AccessCodeAuthorization[];

        return result.map(p => ({ Code: p.Code, Description: p.Description }));
    } catch (err) {
        throw new Error("Error fetching paragraphs: " + String(err));
    }
};

export const getAccessGroups = async (
    documentArchive: number,
    accessCode: number,
    selectedAccessGroup?: number
): Promise<AccessGroupResult[]> => {
    const headers = await getHeaders();
    try {
        const result = await fetchClient(getEnvironment360BizURL() +
            ApiConfig.GetAccessGroups, {
            headers: headers,
            body: JSON.stringify({ documentArchive: documentArchive, accessCode: accessCode, selectedAccessGroup: selectedAccessGroup }),
            method: "POST",
            ...getFetchApiOptions()
        }) as AccessGroupResultType[];

        return result.map(ag => ({
            Recno: ag.Recno,
            Name: ag.Code,
            IsPublic: ag.IsVisibleForAllUsers === -1
        }));

    } catch (err) {
        throw new Error("Error fetching access groups: " + String(err));
    }
};
